import * as THREE from 'three';
import { Entity } from './Entity.js';
import { CONFIG } from '../config.js';

/**
 * @typedef {Object} LegJoint
 * @property {THREE.Group} upper
 * @property {THREE.Group} lower
 */

/**
 * @typedef {Object} CowOptions
 * @property {THREE.Vector3} [position]
 * @property {number} [rotation]
 * @property {number} [speed]
 */

export class Cow extends Entity {
    /** @type {'normal' | 'burning' | 'scorched'} */
    #burnState = 'normal';

    /** @type {number} */
    #burnTimeRemaining = 0;

    /** @type {number} */
    #panicSpeedMultiplier = 6.0;

    /** @type {number} */
    #speed;

    /** @type {number} */
    #timeSinceLastTurn = 0;

    /** @type {boolean} */
    #isMoving = true;

    /** @type {number} */
    #walkCycle = Math.random() * Math.PI * 2;

    /** @type {LegJoint[]} */
    #legJoints = [];

    /** @type {THREE.Mesh} */
    #tail = /** @type {THREE.Mesh} */ (/** @type {unknown} */ (null));

    /**
     * @param {CowOptions} [options]
     */
    constructor(options = {}) {
        super({
            position: options.position,
            rotation: options.rotation,
            collisionRadius: CONFIG.cow.collisionRadius,
            collidable: true,
        });

        this.#speed = options.speed ?? (CONFIG.cow.minSpeed + Math.random() * (CONFIG.cow.maxSpeed - CONFIG.cow.minSpeed));
        this.#buildMesh();
    }

    /** @returns {number} */
    get speed() {
        return this.#speed;
    }

    /** @returns {LegJoint[]} */
    get legJoints() {
        return this.#legJoints;
    }

    /** @returns {boolean} */
    get burned() {
        return this.#burnState !== 'normal';
    }

    /** @returns {boolean} */
    get burning() {
        return this.#burnState === 'burning';
    }

    /** @returns {boolean} */
    get scorched() {
        return this.#burnState === 'scorched';
    }

    /**
     * @returns {boolean}
     */
    burn() {
        if (this.#burnState !== 'normal') {
            return false;
        }

        this.#burnState = 'burning';
        this.#burnTimeRemaining = 7.0;
        this.#isMoving = true;
        this.#timeSinceLastTurn = 0;
        this.#setBurningVisual();
        return true;
    }

    #buildMesh() {
        const bodyGeometry = new THREE.BoxGeometry(2.0, 1.2, 3.0);
        const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.8 });
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        body.position.y = 1.45;
        body.castShadow = true;
        body.receiveShadow = true;
        this.mesh.add(body);

        const headGeometry = new THREE.BoxGeometry(1.0, 1.0, 1.2);
        const headMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        head.position.set(0, 1.8, 1.5);
        head.castShadow = true;
        head.receiveShadow = true;
        this.mesh.add(head);

        const eyeGeometry = new THREE.SphereGeometry(0.15, 8, 8);
        const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(0.3, 0.2, 0.6);
        head.add(leftEye);
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(-0.3, 0.2, 0.6);
        head.add(rightEye);

        const snoutGeometry = new THREE.BoxGeometry(0.8, 0.5, 0.5);
        const snoutMaterial = new THREE.MeshStandardMaterial({ color: 0xFFCBDB });
        const snout = new THREE.Mesh(snoutGeometry, snoutMaterial);
        snout.position.set(0, -0.2, 0.6);
        head.add(snout);

        const earGeometry = new THREE.BoxGeometry(0.3, 0.5, 0.1);
        const earMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const leftEar = new THREE.Mesh(earGeometry, earMaterial);
        leftEar.position.set(0.5, 0.5, 0);
        leftEar.rotation.y = Math.PI / 4;
        head.add(leftEar);
        const rightEar = new THREE.Mesh(earGeometry, earMaterial);
        rightEar.position.set(-0.5, 0.5, 0);
        rightEar.rotation.y = -Math.PI / 4;
        head.add(rightEar);

        this.#addLegs(body);

        const tailGeometry = new THREE.CylinderGeometry(0.1, 0.1, 1.2, 8);
        const tailMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        this.#tail = new THREE.Mesh(tailGeometry, tailMaterial);
        this.#tail.position.set(0, 0.8, -1.7);
        this.#tail.rotation.x = Math.PI / 4;
        this.mesh.add(this.#tail);

        const tuftGeometry = new THREE.SphereGeometry(0.2, 8, 8);
        const tuftMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });
        const tuft = new THREE.Mesh(tuftGeometry, tuftMaterial);
        tuft.position.set(0, -0.6, 0);
        this.#tail.add(tuft);

        if (Math.random() > 0.3) {
            this.#addUdder(body);
        }
    }

    /** @param {THREE.Mesh} body */
    #addLegs(body) {
        const legRadius = 0.12;
        const legSegmentLength = 0.85;
        const upperLegGeometry = new THREE.CylinderGeometry(legRadius, legRadius, legSegmentLength);
        const lowerLegGeometry = new THREE.CylinderGeometry(legRadius, legRadius, legSegmentLength);
        const legMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });

        const legPositions = [
            [0.7, 0, 1.0],
            [-0.7, 0, 1.0],
            [0.7, 0, -1.0],
            [-0.7, 0, -1.0]
        ];

        legPositions.forEach((pos) => {
            const legJoint = new THREE.Group();
            legJoint.position.set(pos[0], body.position.y - 0.6, pos[2]);
            this.mesh.add(legJoint);

            const upperLeg = new THREE.Mesh(upperLegGeometry, legMaterial);
            upperLeg.position.y = -legSegmentLength / 2;
            upperLeg.castShadow = true;
            upperLeg.receiveShadow = true;
            legJoint.add(upperLeg);

            const lowerJoint = new THREE.Group();
            lowerJoint.position.y = -legSegmentLength / 2;
            upperLeg.add(lowerJoint);

            const lowerLeg = new THREE.Mesh(lowerLegGeometry, legMaterial);
            lowerLeg.position.y = -legSegmentLength / 2;
            lowerLeg.castShadow = true;
            lowerLeg.receiveShadow = true;
            lowerJoint.add(lowerLeg);

            this.#legJoints.push({ upper: legJoint, lower: lowerJoint });
        });
    }

    /** @param {THREE.Mesh} body */
    #addUdder(body) {
        const udderGeometry = new THREE.SphereGeometry(0.5, 8, 8);
        const udderMaterial = new THREE.MeshStandardMaterial({ color: 0xFFCBDB });
        const udder = new THREE.Mesh(udderGeometry, udderMaterial);
        udder.position.set(0, 0, -1.2);
        udder.scale.set(1, 0.7, 1);
        body.add(udder);

        const teatGeometry = new THREE.CylinderGeometry(0.07, 0.07, 0.2, 8);
        const teatMaterial = new THREE.MeshStandardMaterial({ color: 0xFFBDBD });
        const teatPositions = [[0.2, -0.3, 0.2], [-0.2, -0.3, 0.2], [0.2, -0.3, -0.2], [-0.2, -0.3, -0.2]];

        teatPositions.forEach(pos => {
            const teat = new THREE.Mesh(teatGeometry, teatMaterial);
            teat.position.set(pos[0], pos[1], pos[2]);
            udder.add(teat);
        });
    }

    #setBurningVisual() {
        this.mesh.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
                return;
            }

            const material = child.material;
            /** @param {unknown} mat */
            const ignite = (mat) => {
                if (!(mat instanceof THREE.MeshStandardMaterial)) {
                    return;
                }

                mat.emissive.setHex(0xff5a00);
                mat.emissiveIntensity = 0.35;
            };

            if (Array.isArray(material)) {
                material.forEach(ignite);
            } else {
                ignite(material);
            }
        });
    }

    #setScorchedVisual() {
        this.mesh.rotation.z = Math.PI;
        this.position.y = 2.15;
        this.#isMoving = false;

        this.mesh.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
                return;
            }

            const material = child.material;
            /** @param {unknown} mat */
            const scorch = (mat) => {
                if (!(mat instanceof THREE.MeshStandardMaterial)) {
                    return;
                }

                mat.color.setHex(0x2b2b2b);
                mat.emissive.setHex(0x1a0500);
                mat.emissiveIntensity = 0.15;
            };

            if (Array.isArray(material)) {
                material.forEach(scorch);
            } else {
                scorch(material);
            }
        });
    }

    /**
     * @param {number} delta
     * @param {number} boundary
     * @param {any[]} collidableObjects
     */
    // @ts-ignore - Cow update has extended signature compared to Entity base class
    update(delta, boundary, collidableObjects) {
        if (this.#burnState === 'burning') {
            this.#burnTimeRemaining -= delta;
            this.#timeSinceLastTurn += delta;

            if (Math.random() < 0.25) {
                this.rotation += (Math.random() - 0.5) * 0.55;
            }

            if (this.#burnTimeRemaining <= 0) {
                this.#burnState = 'scorched';
                this.#setScorchedVisual();
            }
        }

        if (!this.#isMoving) return;

        this.#walkCycle += delta * 2;

        const direction = new THREE.Vector3(0, 0, 1);
        direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.rotation);

        const moveSpeed = this.#burnState === 'burning'
            ? this.#speed * this.#panicSpeedMultiplier
            : this.#speed;

        const newPosition = this.position.clone().add(direction.multiplyScalar(moveSpeed * delta * 60));

        let hitBoundary = false;

        if (Math.abs(newPosition.x) > boundary) {
            hitBoundary = true;
            newPosition.x = Math.sign(newPosition.x) * boundary;
        }

        if (Math.abs(newPosition.z) > boundary) {
            hitBoundary = true;
            newPosition.z = Math.sign(newPosition.z) * boundary;
        }

        let collision = false;
        const startX = this.position.x;
        const startZ = this.position.z;
        const moveX = newPosition.x - startX;
        const moveZ = newPosition.z - startZ;
        const moveLenSq = moveX * moveX + moveZ * moveZ;

        for (const obj of collidableObjects) {
            if (obj === this || !obj.collisionRadius) continue;

            const minDist = this.collisionRadius + obj.collisionRadius;

            let closestX = newPosition.x;
            let closestZ = newPosition.z;
            if (moveLenSq > 0) {
                const t = Math.max(
                    0,
                    Math.min(
                        1,
                        ((obj.position.x - startX) * moveX + (obj.position.z - startZ) * moveZ) / moveLenSq
                    )
                );
                closestX = startX + moveX * t;
                closestZ = startZ + moveZ * t;
            }

            const dx = closestX - obj.position.x;
            const dz = closestZ - obj.position.z;
            const distSq = dx * dx + dz * dz;

            if (distSq < minDist * minDist) {
                collision = true;
                break;
            }
        }

        this.#timeSinceLastTurn += delta;

        if (hitBoundary || collision || (this.#timeSinceLastTurn > CONFIG.cow.turnInterval && Math.random() < 0.01)) {
            if (hitBoundary || collision) {
                this.rotation += Math.PI + (Math.random() - 0.5) * Math.PI;
            } else {
                const randomAngle = (Math.random() * 10 + 5) * (Math.PI / 180);
                this.rotation += Math.random() < 0.5 ? -randomAngle : randomAngle;
            }
            this.#timeSinceLastTurn = 0;
        } else {
            this.position.copy(newPosition);

            if (Math.random() < 0.05) {
                this.rotation += (Math.random() - 0.5) * 0.1;
            }
        }

        this.#animateLegs();
        this.#animateTail();
    }

    #animateLegs() {
        this.#legJoints.forEach((joint, index) => {
            const legOffset = index % 2 === 0 ? 0 : Math.PI;
            const frontBackOffset = index < 2 ? 0 : Math.PI;
            const cycle = this.#walkCycle + legOffset + frontBackOffset;
            joint.upper.rotation.x = Math.sin(cycle) * 0.3;
            joint.lower.rotation.x = Math.sin(cycle + Math.PI / 2) * 0.2;
        });
    }

    #animateTail() {
        this.#tail.rotation.z = Math.sin(Date.now() * 0.002) * 0.2;
    }
}
